import t from "@/lang/lang";
import { useState } from "react";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Button } from "@/shared/components/ui/button";
import {
  Offer,
  OfferPriceVisibility,
} from "@/services/backend/qpm/offers/types/offer";
import { useQpmOfferSetContractTermsMutation } from "@/services/backend/qpm/offers/api/offer-api";
import { createSetContractTermsRequestFromOffer } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/contract-terms-request";
import { InfoIconTooltip } from "@/shared/components/domain/qpm/info-icon-tooltip";
import { resolvedPriceAvailability } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/price-visibility";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Pencil, X } from "lucide-react";

type PriceVisibilitySettingsProps = {
  offer: Offer;
};

type VisibilityRow = {
  key: string;
  label: string;
  checked: boolean;
  available: boolean;
  onChange: (checked: boolean) => OfferPriceVisibility;
};

export function PriceVisibilitySettings({
  offer,
}: PriceVisibilitySettingsProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [setContractTerms, { isLoading }] =
    useQpmOfferSetContractTermsMutation();
  const disabled = offer.immutable || isLoading;
  const visibility =
    createSetContractTermsRequestFromOffer(offer).priceVisibility;
  const availability = resolvedPriceAvailability(offer);

  const submitVisibility = (nextVisibility: OfferPriceVisibility) => {
    setContractTerms({
      ...createSetContractTermsRequestFromOffer(offer),
      priceVisibility: nextVisibility,
    });
  };

  const rows: VisibilityRow[] = [
    {
      key: "material-qpm",
      label: t("Anschaffung QPM"),
      checked: availability.material.qpm && visibility.material.qpm,
      available: availability.material.qpm,
      onChange: (checked) => ({
        ...visibility,
        material: { ...visibility.material, qpm: checked },
      }),
    },
    {
      key: "material-fiv",
      label: t("Anschaffung FIV"),
      checked: availability.material.fiv && visibility.material.fiv,
      available: availability.material.fiv,
      onChange: (checked) => ({
        ...visibility,
        material: { ...visibility.material, fiv: checked },
      }),
    },
    {
      key: "material-alora",
      label: t("Anschaffung ALoRa"),
      checked: availability.material.aLoRa && visibility.material.aLoRa,
      available: availability.material.aLoRa,
      onChange: (checked) => ({
        ...visibility,
        material: { ...visibility.material, aLoRa: checked },
      }),
    },
    {
      key: "installation-qpm",
      label: t("Installation QPM"),
      checked: availability.installation.qpm && visibility.installation.qpm,
      available: availability.installation.qpm,
      onChange: (checked) => ({
        ...visibility,
        installation: { ...visibility.installation, qpm: checked },
      }),
    },
    {
      key: "installation-fiv",
      label: t("Installation FIV"),
      checked: availability.installation.fiv && visibility.installation.fiv,
      available: availability.installation.fiv,
      onChange: (checked) => ({
        ...visibility,
        installation: { ...visibility.installation, fiv: checked },
      }),
    },
    {
      key: "installation-alora",
      label: t("Installation ALoRa"),
      checked: availability.installation.aLoRa && visibility.installation.aLoRa,
      available: availability.installation.aLoRa,
      onChange: (checked) => ({
        ...visibility,
        installation: { ...visibility.installation, aLoRa: checked },
      }),
    },
    {
      key: "maintenance-qpm",
      label: t("Service QPM"),
      checked: availability.maintenance.qpm && visibility.maintenance.qpm,
      available: availability.maintenance.qpm,
      onChange: (checked) => ({
        ...visibility,
        maintenance: { ...visibility.maintenance, qpm: checked },
      }),
    },
    {
      key: "maintenance-fiv",
      label: t("Wartung Fluginsektenvernichter"),
      checked: availability.maintenance.fiv && visibility.maintenance.fiv,
      available: availability.maintenance.fiv,
      onChange: (checked) => ({
        ...visibility,
        maintenance: { ...visibility.maintenance, fiv: checked },
      }),
    },
    {
      key: "maintenance-scb",
      label: t("Service SbD"),
      checked: availability.maintenance.scb && visibility.maintenance.scb,
      available: availability.maintenance.scb,
      onChange: (checked) => ({
        ...visibility,
        maintenance: { ...visibility.maintenance, scb: checked },
      }),
    },
    {
      key: "maintenance-alora",
      label: t("ALoRa Service"),
      checked: availability.maintenance.aLoRa && visibility.maintenance.aLoRa,
      available: availability.maintenance.aLoRa,
      onChange: (checked) => ({
        ...visibility,
        maintenance: { ...visibility.maintenance, aLoRa: checked },
      }),
    },
    {
      key: "renting-alora",
      label: t("Mietpreis ALoRa"),
      checked: availability.renting.aLoRa && visibility.renting.aLoRa,
      available: availability.renting.aLoRa,
      onChange: (checked) => ({
        ...visibility,
        renting: { ...visibility.renting, aLoRa: checked },
      }),
    },
  ];

  return (
    <section className="rounded-md border p-3">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold">
            {t("Preispositionen anzeigen")}
          </h4>
          <InfoIconTooltip
            text={t(
              "Diese Schaltflächen blenden berechnete Preispositionen im Angebot ein oder aus. In der Regel sollte das nicht nötig sein, wenn Bestandsaufnahme und Kalkulation passend konfiguriert wurden.",
            )}
          />
        </div>
        {!offer.immutable ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => setIsEditing((current) => !current)}
                disabled={isLoading}
              >
                {isEditing ? (
                  <X className="h-4 w-4" />
                ) : (
                  <Pencil className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-background text-foreground max-w-sm border text-left text-xs">
              {isEditing ? t("Abbrechen") : t("Bearbeiten")}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((row) => (
          <label
            key={row.key}
            className="flex items-center gap-2 text-sm"
            aria-disabled={!row.available || disabled}
          >
            <Checkbox
              checked={row.checked}
              disabled={disabled || !row.available || !isEditing}
              onCheckedChange={(checked) => {
                submitVisibility(row.onChange(checked === true));
              }}
            />
            <span className={!row.available ? "text-muted-foreground" : ""}>
              {row.label}
            </span>
            {!row.available ? (
              <InfoIconTooltip
                text={t(
                  "Diese Position kann nicht aktiviert werden, da keine berechneten Daten vorhanden sind, die im Angebot dargestellt werden könnten.",
                )}
              />
            ) : null}
          </label>
        ))}
      </div>
    </section>
  );
}
